import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { TzProvider } from "@/lib/TzContext";
import { I18nProvider } from "@/lib/I18nContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";
import MiPerfil from "@/pages/MiPerfil";
import Fase1Grupos from "@/pages/Fase1Grupos";
import Fase2Eliminatorias from "@/pages/Fase2Eliminatorias";
import Ranking from "@/pages/Ranking";
import Reglas from "@/pages/Reglas";
import TodosPronosticos from "@/pages/TodosPronosticos";
import NoticiasRoute from "@/pages/NoticiasRoute";
import NoticiaDetalle from "@/pages/NoticiaDetalle";
import Admin from "@/pages/Admin";
import PollasEspeciales from "@/pages/PollasEspeciales";
import "@/App.css";

/** Detect Emergent preview environment and tag <html> so we can offset the BottomNav. */
function PreviewBadgeFix() {
  useEffect(() => {
    const host = window.location.hostname || "";
    const isPreview = host.endsWith("emergentagent.com") || host.endsWith("preview.emergentagent.com");
    if (isPreview) document.documentElement.classList.add("emergent-preview");
    return () => document.documentElement.classList.remove("emergent-preview");
  }, []);
  return null;
}

/** Scroll to top whenever the route changes (so pages always open at their header). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function ProtectedRoute({ children, adminOnly = false, specialAllowed = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-black text-white"><div className="font-display text-2xl uppercase tracking-wider">Cargando…</div></div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/perfil" replace />;
  // Restrict special-only users to /pollas-especiales + /noticias only
  if (user.is_special_only && !user.is_admin && !specialAllowed) {
    return <Navigate to="/pollas-especiales" replace />;
  }
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth callback synchronously (URL fragment)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/noticias" element={<NoticiasRoute />} />
      <Route path="/noticias/:id" element={<NoticiaDetalle />} />
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/perfil" element={<ProtectedRoute><MiPerfil /></ProtectedRoute>} />
      <Route path="/pronosticos" element={<Navigate to="/pronosticos/fase1" replace />} />
      <Route path="/pronosticos/fase1" element={<ProtectedRoute><Fase1Grupos /></ProtectedRoute>} />
      <Route path="/pronosticos/fase2" element={<ProtectedRoute><Fase2Eliminatorias /></ProtectedRoute>} />
      <Route path="/ranking" element={<ProtectedRoute><Ranking /></ProtectedRoute>} />
      <Route path="/reglas" element={<ProtectedRoute><Reglas /></ProtectedRoute>} />
      <Route path="/todos" element={<ProtectedRoute><TodosPronosticos /></ProtectedRoute>} />
      <Route path="/pollas-especiales" element={<ProtectedRoute specialAllowed><PollasEspeciales /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <TzProvider>
        <AuthProvider>
          <BrowserRouter>
            <PreviewBadgeFix />
            <AppRouter />
            <Toaster richColors position="top-right" theme="dark" toastOptions={{ style: { zIndex: 99999 } }} className="!z-[99999]" />
          </BrowserRouter>
        </AuthProvider>
      </TzProvider>
    </I18nProvider>
  );
}
